import React, { useEffect, useState } from "react";
import { AppButton } from "site/ui";
import styled from "styled-components";
import { ICourse, IInterview, IModule, IUserCourse, IUserInterview, IUserQuiz } from "utils/types";


const QuizCard = styled.div`
  background: white;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 35px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const QuizInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const QuizBadge = styled.div`
  background-color: #5575f7;
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 20px;
  width: fit-content;
  text-transform: capitalize;
`;

const QuizTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const QuizDetail = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: 14px;
  color: #6c757d;
  flex: 1;
`;

const Label = styled.span`
  font-weight: 500;
`;

const Value = styled.span`
  color: #6c757d;
`;

const ProgressContainer = styled.div`
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  flex: 1;
  align-items: center;
  display: flex;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => `${progress}%`};
  background-color: ${({ progress }) =>
    progress === 100 ? "#28a745" : "#5575f7"};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ActionButton = styled(AppButton)`

  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

export interface ProgressCardProps {
  course?: ICourse;
  data: (IUserQuiz|IUserCourse|IUserInterview) & { type: "course"|"quiz"|"interview" | string };
  courseId: string;
  onViewQuizReport: (userQuiz: IUserQuiz) => void;
  onTakeQuiz: (courseId: string, quizId: string) => void;
  onContinueCourse: (courseId: string, moduleId: string) => void;
  onGetCourse: (courseId: string) => Promise<ICourse|undefined>
  onGetCourseModuleCount: (courseModule: Partial<IModule>) => Promise<number>;
  onGetInterview: (interviewId: string) => Promise<IInterview|undefined>;
  onRetakeInterview: (interviewId: string, interview?: IInterview) => void;
 }

export const ProgressCard: React.FC<ProgressCardProps> = ({
  courseId, course: initCourse,
  data, onGetCourseModuleCount,
  onViewQuizReport, onGetCourse,
  onContinueCourse, onTakeQuiz,
  onGetInterview, onRetakeInterview,
}) => {
  const [course, setCourse] = useState<ICourse|undefined>(initCourse);
  const [interview, setInterview] = useState<IInterview>()
  const [moduleCount, setModuleCount] = useState(0);
  
  const courseType = data.type;
  let userQuiz:IUserQuiz|undefined;
  let userCourse:IUserCourse|undefined;
  let userInterview:IUserInterview|undefined;
  let progress = 0;
  let hasScore = false;
  let title;
  let score, totalScore = 0;
  if(courseType === "quiz"){
    userQuiz = (data as IUserQuiz);
    progress = 100;
    score = userQuiz.score;
    totalScore = userQuiz.totalScore
    title = userQuiz.quiz?.title || ""
  }
  else if(courseType === "interview"){
    userInterview = (data as IUserInterview);
    progress = 100;
    score = userInterview.score;
    totalScore = userInterview.totalScore
    // title = userInterview.interview?.title
  }
  else {
    userCourse = (data as IUserCourse);
    progress = moduleCount === 0 ? 0 : (userCourse?.completedCourseModuleIds?.length || 0) * 100 / moduleCount;
  }
  title = course?.title || initCourse?.title || interview?.title
  useEffect(() => {
    if(courseType === "interview"){
      return;
    }
    if(courseId && !initCourse){
      onGetCourse(courseId).then(setCourse)
    }
  }, [courseId, JSON.stringify(initCourse), courseType])
  useEffect(() => {  
    if(courseId){
      if(courseType === "interview"){
        onGetInterview(courseId).then(setInterview)
        return;
      }
      onGetCourseModuleCount({
        courseId
      }).then(setModuleCount)
    }
  }, [courseId, courseType])

  return <QuizCard>
    <QuizInfo>
      <QuizBadge>{courseType}</QuizBadge>
      <QuizTitle>{title || ""}</QuizTitle>
      <div style={{display: "flex", gap: "10px", flexDirection: "column"}}>
      <QuizDetail>
          <Label>Progress:</Label>
          <ProgressContainer>
            <ProgressBar progress={progress} />
          </ProgressContainer>
      </QuizDetail>
      {hasScore && (<>
          <QuizDetail>
              <Label>Score:</Label>
              <Value>{score || 0} / {totalScore || 0}</Value>
          </QuizDetail>
          {userQuiz && <QuizDetail>
              <Label>Quiz mode:</Label>
              <Value>{userQuiz.quizMode}</Value>
          </QuizDetail>}
        </>)}
      </div>
    </QuizInfo>
    {courseType === "quiz"  && (
      <div style={{ display: "flex", gap: "10px" }}>
          <ActionButton variant="secondary" onClick={() => {
            if(userQuiz?.quizId) {
              onTakeQuiz(courseId, userQuiz.quizId)
            }
          }}>Re-take quiz</ActionButton>
          <ActionButton variant="text" onClick={() => {
            if(userQuiz){
              onViewQuizReport(userQuiz)
            }
          }}>View result</ActionButton>
      </div>          
    )} 
    {courseType === "course" && (
        <div>
          {userCourse?.progress === 100 ? 
            <ActionButton onClick={() => () => {
              if(userQuiz?.quizId) {
                onTakeQuiz(courseId, userQuiz.quizId)
              }
            }} variant="text">Take quiz</ActionButton> : 
            <ActionButton onClick={() => {
              if(userCourse?.currentModuleId){
                onContinueCourse(courseId, userCourse.currentModuleId);
              }
            }} variant="text">{progress === 100 ? "Retake course" : "Continue course"}</ActionButton>}
        </div>
    )}
    {courseType === "interview"  && (
      <div style={{ display: "flex", gap: "10px" }}>
          <ActionButton variant="secondary" onClick={() => {
            if(userInterview?.interviewId) {
              onRetakeInterview(userInterview?.interviewId, interview)
            }
          }}>Re-take interview</ActionButton>
          {/* <ActionButton variant="text" onClick={() => {
            if(userQuiz){
              onViewQuizReport(userQuiz)
            }
          }}>View result</ActionButton> */}
      </div>          
    )} 
  </QuizCard>
};